import { resolveMembershipType } from '../membership';

const hasText = (value) => (typeof value === 'string' ? value.trim().length > 0 : Boolean(value));

export function getDealCardMembershipType(user) {
  return resolveMembershipType(user);
}

export function formatNumberCompact(num) {
  if (num == null || Number.isNaN(Number(num))) return '';
  const n = Number(num);
  const abs = Math.abs(n);

  const format = (value, suffix) => {
    const rounded = (Math.round(value * 10) / 10).toFixed(1);
    return `${rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded}${suffix}`;
  };

  if (abs >= 1e12) return format(n / 1e12, 't');
  if (abs >= 1e9) return format(n / 1e9, 'b');
  if (abs >= 1e6) return format(n / 1e6, 'm');
  if (abs >= 1e3) return format(n / 1e3, 'k');

  return String(Math.round(n));
}

export function formatCurrencyCompact(currency, amount) {
  if (!currency || amount == null) return '';
  const n = Number(amount);
  if (Number.isNaN(n)) return `${currency} ${amount}`;
  return `${currency} ${formatNumberCompact(n)}`;
}

export function formatDateForSurface(date, locale = 'en-US') {
  if (!date) return '';

  const formatOptions =
    locale === 'en-GB'
      ? { day: 'numeric', month: 'short', year: 'numeric' }
      : { day: 'numeric', month: 'long', year: 'numeric' };

  return new Date(date).toLocaleDateString(locale, formatOptions);
}

export function extractIndustries(industryInfo) {
  return Array.from(
    new Set(
      (Array.isArray(industryInfo) ? industryInfo : [])
        .flatMap((item) => {
          if (!item) return [];
          if (typeof item === 'string') return [item];
          if (typeof item === 'object') {
            return [
              item.industry_category,
              item.industry,
              item.name,
              item.category,
            ].filter(Boolean);
          }
          return [];
        })
        .map((value) => String(value).trim())
        .filter(Boolean)
    )
  );
}

export function normalizeParagraphs(rawText) {
  return String(rawText || '')
    .split('[[NEW_PARAGRAPH]]')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

export function stripHtml(value) {
  return String(value || '').replace(/<[^>]+>/g, '');
}

export function createArticleModel(rawText, articleConfig = {}) {
  if (!articleConfig.enabled) {
    return {
      summary: undefined,
      detailParagraphs: [],
      paragraphs: [],
      hasArticle: false,
    };
  }

  const paragraphs = normalizeParagraphs(rawText);
  if (!paragraphs.length) {
    return {
      summary: undefined,
      detailParagraphs: [],
      paragraphs: [],
      hasArticle: false,
    };
  }

  const expandedParagraphs =
    articleConfig.expandedParagraphs === 'all'
      ? paragraphs
      : paragraphs.slice(0, Math.max(0, Number(articleConfig.expandedParagraphs) || 0));

  if (!expandedParagraphs.length) {
    return {
      summary: undefined,
      detailParagraphs: [],
      paragraphs,
      hasArticle: false,
    };
  }

  return {
    summary: stripHtml(expandedParagraphs[0]) || undefined,
    detailParagraphs: expandedParagraphs.slice(1),
    paragraphs,
    hasArticle: true,
  };
}

export function getDealCardTitle(deal, surfaceConfig) {
  if (surfaceConfig.titleSource === 'recipientCompanyOrFallback') {
    const companyName = hasText(deal.recipient_company)
      ? deal.recipient_company.trim()
      : surfaceConfig.titleFallback || 'Company not listed';

    return {
      title: companyName,
      titleText: companyName,
      titleUrl: hasText(deal.recipient_company_url) ? deal.recipient_company_url.trim() : null,
    };
  }

  const title = hasText(deal.title) ? deal.title.trim() : '—';
  return {
    title,
    titleText: title,
    titleUrl: null,
  };
}

export function getParticipationType(deal) {
  if (deal?.lead && deal?.participated) return 'both';
  if (deal?.lead) return 'lead';
  if (deal?.participated) return 'participated';
  return null;
}

export function getParticipationLabel(type, t) {
  if (type === 'lead') {
    return t('pageInvProf.labelLead');
  }
  if (type === 'participated') {
    return t('pageInvProf.labelSecond');
  }
  if (type === 'both') {
    return t('pageInvProf.labelBoth');
  }
  return '';
}

export function getParticipationBadgeVariant(type) {
  if (type === 'lead') return 'v5';
  if (type === 'participated') return 'v6';
  if (type === 'both') return 'v7';
  return 'v1';
}

export function hasRecipientCompanyData(deal) {
  if (typeof deal?.has_recipient_company_info === 'boolean') {
    return deal.has_recipient_company_info;
  }
  return hasText(deal?.recipient_company) || hasText(deal?.recipient_company_url);
}

export function mapInvestorGroups({
  deal,
  investorConfig,
  buildInvestorNode,
  t,
}) {
  const leadIds = Array.isArray(deal.lead_investors) ? deal.lead_investors : [];
  const otherIds = Array.isArray(deal.other_investors_participated)
    ? deal.other_investors_participated
    : [];

  const mapNodes = (ids, group) =>
    ids.map((id, index) =>
      typeof buildInvestorNode === 'function'
        ? buildInvestorNode(id, index, ids.length, {
            deal,
            group,
            index,
            total: ids.length,
          })
        : id
    );

  if (investorConfig.mode === 'none') {
    return { leadInvestors: [], otherInvestors: [], investorsTotal: undefined };
  }

  if (investorConfig.mode === 'countOnly') {
    const uniqueIds = Array.from(new Set([...leadIds, ...otherIds]));
    return {
      leadInvestors: [],
      otherInvestors: [],
      investorsTotal: uniqueIds.length,
    };
  }

  if (investorConfig.mode === 'merged') {
    const mergedIds = Array.from(new Set([...leadIds, ...otherIds]));
    return {
      leadInvestors: mapNodes(mergedIds, 'merged'),
      otherInvestors: [],
      investorsTotal: mergedIds.length,
    };
  }

  const leadInvestors = mapNodes(leadIds, 'lead');
  const otherInvestors = mapNodes(otherIds, 'other');

  if (!leadInvestors.length && investorConfig.fallbackLeadKey) {
    leadInvestors.push(
      t(investorConfig.fallbackLeadKey, {
        defaultValue: investorConfig.fallbackLeadDefault || 'Not listed',
      })
    );
  }

  return {
    leadInvestors,
    otherInvestors,
    investorsTotal: Array.from(new Set([...leadIds, ...otherIds])).length || undefined,
  };
}
